import React from 'react';
import { useFullscreenOverlay } from './FullscreenOverlayContext';
import {
  header,
  headerColumn1,
  headerColumn2,
  headerColumn3,
  headerColumns,
  headerSubtitle,
  headerTitle,
  mediaCloseButtonInline,
  overlayBackButton,
} from './fullscreenOverlay.styles';
import { useScrollFadeHeader } from '../../../pages/HaloListingPage/hooks/useScrollFadeHeader';
import { CloseButton } from '../CloseButton';
import { ChevronLeftIcon } from '../icons';
import type { FullscreenOverlayHeaderProps } from './types';
import type { CSSProperties } from 'react';

export const FullscreenOverlayHeader = ({
  title,
  subtitle,
  actions,
  showCloseButton = true,
  closeLabel,
  layout = 'sticky',
  variant = 'standard',
  scrollContainerRef,
}: FullscreenOverlayHeaderProps) => {
  const { close } = useFullscreenOverlay();

  const shouldApplyScrollFade = false;
  const { backgroundOpacity } = useScrollFadeHeader({
    enabled: shouldApplyScrollFade,
    scrollContainerRef,
  });

  const handleCloseClick = () => {
    close();
  };

  const dynamicHeaderStyles: CSSProperties = shouldApplyScrollFade
    ? {
        backgroundColor: `rgba(255, 255, 255, ${backgroundOpacity})`,
        borderBottomColor: backgroundOpacity > 0.8 ? '#d8d8d8' : 'transparent',
        borderBottomWidth: backgroundOpacity > 0.8 ? '1px' : '0px',
        borderBottomStyle: 'solid' as const,
        transition: 'background-color 0.2s ease-out, border-bottom 0.2s ease-out',
      }
    : {};

  if (variant === 'media' && layout === 'floating') {
    if (actions) {
      return (
        <header className={header({ layout: 'floating', variant: 'media' })}>
          <div className={headerColumns}>
            <div className={headerColumn1}>
              <CloseButton onClick={handleCloseClick} aria-label="Close overlay" className={mediaCloseButtonInline} />
            </div>
            <div className={headerColumn2} />
            <div className={headerColumn3}>{actions}</div>
          </div>
        </header>
      );
    }
    return <CloseButton variant="floating" onClick={handleCloseClick} aria-label="Close overlay" />;
  }

  return (
    <header className={header({ layout, variant })} style={dynamicHeaderStyles}>
      <div className={headerColumns}>
        <div className={headerColumn1}>
          {showCloseButton &&
            (closeLabel ? (
              <button className={overlayBackButton} onClick={handleCloseClick} aria-label={closeLabel}>
                <ChevronLeftIcon />
                <span>{closeLabel}</span>
              </button>
            ) : (
              <CloseButton onClick={handleCloseClick} aria-label="Close overlay" />
            ))}
        </div>

        <div className={headerColumn2}>
          {title && (
            <h2 id="overlay-title" className={headerTitle}>
              {title}
            </h2>
          )}
          {subtitle && <p className={headerSubtitle}>{subtitle}</p>}
        </div>

        <div className={headerColumn3}>{actions}</div>
      </div>
    </header>
  );
};
